import React, { createContext, useEffect, useState, useContext, useRef, useCallback } from "react";
import Endpoint from "../Endpoint";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [lastMessage, setLastMessage] = useState(null);
    const reconnectTimeout = useRef(null);
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 10;
    const isConnecting = useRef(false);
    const isMounted = useRef(true);
    const socketRef = useRef(null);

    const convertirHttpAWs = (url) => {
        if (typeof url !== 'string') return null;

        if (url.startsWith('https://')) {
            return url.replace(/^https:/, 'wss:');
        } else if (url.startsWith('http://')) {
            return url.replace(/^http:/, 'ws:');
        } else {
            return url;
        }
    };

    const connectWebSocket = useCallback(() => {
        // Prevenir múltiples conexiones simultáneas
        if (isConnecting.current) {
            return;
        }

        if (reconnectAttempts.current >= maxReconnectAttempts) {
            console.warn("⚠️ Límite de reconexiones alcanzado. WebSocket deshabilitado.");
            return;
        }

        isConnecting.current = true;

        try {
            const wsUrl = convertirHttpAWs(Endpoint);
            if (!wsUrl) {
                console.warn("⚠️ URL de WebSocket no válida. WebSocket deshabilitado.");
                isConnecting.current = false;
                return;
            }

            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                if (!isMounted.current) {
                    ws.close();
                    return;
                }
                reconnectAttempts.current = 0;
                clearTimeout(reconnectTimeout.current);
                isConnecting.current = false;
                socketRef.current = ws;
                setSocket(ws);
            };

            ws.onmessage = (event) => {
                if (!isMounted.current) return;
                try {
                    const data = JSON.parse(event.data);
                    setMessages((prev) => [...prev, data]);
                    setLastMessage(data);
                } catch (error) {
                    console.error("❌ Error al procesar mensaje:", error, event.data);
                }
            };

            ws.onerror = (error) => {
                if (!isMounted.current) return;
                // No loguear cada error para evitar spam en consola
                isConnecting.current = false;
            };

            ws.onclose = (event) => {
                if (!isMounted.current) return;
                
                isConnecting.current = false;
                socketRef.current = null;
                setSocket(null);

                // Solo intentar reconectar si no fue un cierre intencional
                if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
                    reconnectAttempts.current += 1;
                    reconnectTimeout.current = setTimeout(() => {
                        if (isMounted.current) {
                            connectWebSocket();
                        }
                    }, Math.min(3000 * reconnectAttempts.current, 30000)); // Backoff exponencial con límite
                } else if (reconnectAttempts.current >= maxReconnectAttempts) {
                    console.warn("⚠️ Límite de reconexiones alcanzado. WebSocket deshabilitado.");
                }
            };
        } catch (error) {
            console.error("❌ Error al crear WebSocket:", error);
            isConnecting.current = false;
            
            if (isMounted.current && reconnectAttempts.current < maxReconnectAttempts) {
                reconnectAttempts.current += 1;
                reconnectTimeout.current = setTimeout(() => {
                    if (isMounted.current) {
                        connectWebSocket();
                    }
                }, Math.min(3000 * reconnectAttempts.current, 30000));
            }
        }
    }, []);

    useEffect(() => {
        isMounted.current = true;
        connectWebSocket();

        return () => {
            isMounted.current = false;
            if (socketRef.current) {
                socketRef.current.close(1000, 'Component unmounting');
                socketRef.current = null;
            }
            clearTimeout(reconnectTimeout.current);
            isConnecting.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Solo ejecutar una vez al montar

    const sendMessage = (message) => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "chat", message }));
        } else {
            console.warn("⚠️ No se puede enviar mensaje, WebSocket no está conectado.");
        }
    };

    return (
        <WebSocketContext.Provider value={{ messages, lastMessage, sendMessage }}>
            {children}
        </WebSocketContext.Provider>
    );
};

// Hook personalizado
export const useWebSocket = () => {
    return useContext(WebSocketContext);
};